import { useState, useEffect } from 'react';
import useText from '@/hooks/useText';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Autoplay } from 'swiper/modules';

export default function HomeStats() {
  const labelPublications = useText('home.stats.label_publications');
  const labelEvidences = useText('home.stats.label_evidences');
  const labelGDADiseases = useText('home.stats.label_gda_diseases');
  const labelDiseaseGenes = useText('home.stats.label_disease_genes');
  const labelDiseaseVariants = useText('home.stats.label_disease_variants');
  const labelCitedPapers = useText('home.stats.label_cited_papers');

  const defaultStats = [
    { value: '1.6M+', label: labelPublications, iconSrc: '/static/images/icons/papers.svg' },
    { value: '13.3M+', label: labelEvidences, iconSrc: '/static/images/icons/evidences.svg' },
    { value: '38.2K+', label: labelGDADiseases, iconSrc: '/static/images/icons/iconsPink/013.svg' },
    { value: '30.4K+', label: labelDiseaseGenes, iconSrc: '/static/images/icons/diseases-traits.svg' },
    { value: '1.7M+', label: labelDiseaseVariants, iconSrc: '/static/images/icons/disease-variants.svg' },
  ];

  const [stats, setStats] = useState(defaultStats);

  useEffect(() => {
    fetch('/stats.json')
      .then(res => res.json())
      .then(data => {
        const currentVersion = data.current;
        const s = data[currentVersion]?.statistics;
        if (!s) return;
        const format = (num: number | undefined, suffix = '+') => {
          const n = num ?? 0;
          if (n <= 0) return null;
          return n >= 1e6
            ? `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}M${suffix}`
            : n >= 1e3
              ? `${(n / 1e3).toFixed(1).replace(/\.0$/, '')}K${suffix}`
              : `${n}${suffix}`;
        };
        setStats([
          {
            value: format(s.publication ?? 0) ?? defaultStats[0].value,
            label: labelPublications,
            iconSrc: defaultStats[0].iconSrc,
          },
          {
            value: format(s.evidence ?? 0) ?? defaultStats[1].value,
            label: labelEvidences,
            iconSrc: defaultStats[1].iconSrc,
          },
          {
            value: format(s.gda_disease ?? s.disease ?? 0) ?? defaultStats[2].value,
            label: labelGDADiseases,
            iconSrc: defaultStats[2].iconSrc,
          },
          {
            value: format(s.gda_gene ?? 0) ?? defaultStats[3].value,
            label: labelDiseaseGenes,
            iconSrc: defaultStats[3].iconSrc,
          },
          {
            value: format(s.variant ?? 0) ?? defaultStats[4].value,
            label: labelDiseaseVariants,
            iconSrc: defaultStats[4].iconSrc,
          },
          {
            value: format(s.gda_publication ?? 0) ?? defaultStats[5].value,
            label: labelCitedPapers,
            iconSrc: defaultStats[5].iconSrc,
          },
        ]);
      })
      .catch(() => {});
  }, [labelPublications, labelDiseaseGenes, labelCitedPapers, labelEvidences, labelGDADiseases]);

  return (
    <div className="home-stats-container">
      <Swiper
        modules={[Autoplay]}
        spaceBetween={10}
        slidesPerView={4}
        loop={true}
        autoplay={{ delay: 3000 }}
        breakpoints={{
          320: { slidesPerView: 2 },
          640: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
        }}
      >
        {stats.map(({ value, label, iconSrc }) => (
          <SwiperSlide key={label}>
            <div className="home-stats-card home-stats-card-row">
              <div className="home-stats-icon-wrap">
                <img src={iconSrc} className="home-stats-image-icon" alt={label} />
              </div>
              <div className="home-stats-text">
                <div className="home-stats-value">{value}</div>
                <div className="home-stats-label">{label}</div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}
